import { FieldTypes } from '../../../constants/FieldTypes'
import {
  CheckboxField,
  DateTimeField,
  DropdownField,
  FieldMap,
  FieldMapValue,
  TextAreaField,
  TextField,
} from '../../../types'

const admittedAtField: DateTimeField = {
  type: FieldTypes.DATETIME,
  placeholder: 'Admission Date & Time',
  defaultToNow: true,
  withNowButton: true,
}

// Provider fields: options populated at runtime in BirthingCenterAdmissionInput
const admittingMidwifeField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Admitting Midwife',
  searchable: true,
  options: [],
}

const admissionTypeField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Admission Type',
  inputType: 'radio',
  options: [
    { id: 'spontaneous-labor', text: 'Spontaneous Labor' },
    { id: 'prom', text: 'PROM (Premature Rupture of Membranes)' },
    { id: 'elective', text: 'Elective' },
    { id: 'induction', text: 'Induction' },
    { id: 'transfer-in', text: 'Transfer In' },
  ],
}

const membraneStatusField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Membrane Status',
  inputType: 'radio',
  options: [
    { id: 'intact', text: 'Intact' },
    { id: 'srom', text: 'SROM (Spontaneous Rupture)' },
    { id: 'arom', text: 'AROM (Artificial Rupture)' },
  ],
}

const isRuptured = (v?: FieldMapValue) => !!v?.membraneStatus && v.membraneStatus !== 'intact'

const membraneRuptureTimeField: DateTimeField = {
  type: FieldTypes.DATETIME,
  placeholder: 'Time of Rupture',
  optional: true,
  condition: isRuptured,
}

const membraneFluidColorField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Fluid Color',
  inputType: 'radio',
  optional: true,
  options: [
    { id: 'clear', text: 'Clear' },
    { id: 'bloody', text: 'Bloody' },
    { id: 'meconium-thin', text: 'Meconium – Thin' },
    { id: 'meconium-thick', text: 'Meconium – Thick' },
  ],
  condition: isRuptured,
}

const membraneFluidAmountField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Fluid Amount',
  inputType: 'radio',
  optional: true,
  options: [
    { id: 'scant', text: 'Scant' },
    { id: 'moderate', text: 'Moderate' },
    { id: 'large', text: 'Large' },
  ],
  condition: isRuptured,
}

// §3.1: Referring provider — Tier 2, conditional on Transfer In admission type.
const referringProviderField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Referring Provider',
  optional: true,
  condition: (v?: FieldMapValue) => v?.admissionType === 'transfer-in',
}

const lastOralIntakeTimeField: DateTimeField = {
  type: FieldTypes.DATETIME,
  placeholder: 'Last Oral Intake — Time',
  optional: true,
}

const lastOralIntakeContentField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Last Oral Intake — Content',
  optional: true,
}

const birthPlanOnFileField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Birth Plan on File',
  optional: true,
  options: [
    { id: 'yes', text: 'Yes' },
    { id: 'no', text: 'No' },
    { id: 'in-progress', text: 'In Progress' },
  ],
}

// Provider field: options populated at runtime in BirthingCenterAdmissionInput
const supportPersonsField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Support Persons',
  searchable: true,
  allowMultiple: true,
  options: [],
}

// Provider field: options populated at runtime in BirthingCenterAdmissionInput
const additionalProvidersPresentField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Additional Providers Present',
  searchable: true,
  allowMultiple: true,
  optional: true,
  options: [],
}

const supervisoryConsultationNoteField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Supervisory consultation notes...',
  optional: true,
}

const supervisoryConsultationField: FieldMap = {
  name: 'Supervisory Consultation',
  optional: true,
  children: {
    note: supervisoryConsultationNoteField,
  },
}

const interpreterNeededField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Interpreter Needed',
  optional: true,
  inputType: 'switch',
}

const interpreterLanguageField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Language',
  optional: true,
  condition: (v?: FieldMapValue) => !!v?.interpreterNeeded,
}

export const admissionSectionField: FieldMap = {
  name: 'Birthing Center Admission',
  children: {
    admittedAt: admittedAtField,
    admittingMidwife: admittingMidwifeField,
    admissionType: admissionTypeField,
    referringProvider: referringProviderField,
    membraneStatus: membraneStatusField,
    membraneRuptureTime: membraneRuptureTimeField,
    membraneFluidColor: membraneFluidColorField,
    membraneFluidAmount: membraneFluidAmountField,
    lastOralIntakeTime: lastOralIntakeTimeField,
    lastOralIntakeContent: lastOralIntakeContentField,
    birthPlanOnFile: birthPlanOnFileField,
    supportPersons: supportPersonsField,
    additionalProvidersPresent: additionalProvidersPresentField,
    interpreterNeeded: interpreterNeededField,
    interpreterLanguage: interpreterLanguageField,
    supervisoryConsultation: supervisoryConsultationField,
  },
}
